import React from 'react';
import { Box, Typography } from '@mui/material';
import { FieldType, SectionFieldDTO } from '../../magic-report.type';
import { BooleanFieldRenderer } from './BooleanFieldRenderer';
import { SelectFieldRenderer } from './SelectFieldRenderer';
import { MultiselectFieldRenderer } from './MultiselectFieldRenderer';
import { TextFieldRenderer } from './TextFieldRenderer';
import { TextArrayFieldRenderer } from './TextArrayFieldRenderer';
import { DateFieldRenderer } from './DateFieldRenderer';
import { NumberFieldRenderer } from './NumberFieldRenderer';

interface FieldRendererFactoryProps {
  field: SectionFieldDTO;
  onChange: (updatedField: SectionFieldDTO) => void;
  options?: string[]; // For Select/Multiselect types
}

export const FieldRendererFactory: React.FC<FieldRendererFactoryProps> = ({ field, onChange, options = [] }) => {
  const handleSimpleFieldChange = (value: string | null, addToActionItems: boolean) => {
    onChange({
      ...field,
      value: value || '',
      addToActionItems,
    });
  };

  const handleEntriesChange = (entries: any[]) => {
    onChange({
      ...field,
      entries,
    });
  };

  // Normalize field type to number if it's a string (for backward compatibility)
  let fieldType = field.fieldType;
  if (typeof fieldType === 'string') {
    const typeMap: Record<string, FieldType> = {
      Boolean: FieldType.Boolean,
      Select: FieldType.Select,
      Multiselect: FieldType.Multiselect,
      Text: FieldType.Text,
      TextArray: FieldType.TextArray,
      Date: FieldType.Date,
      Number: FieldType.Number,
    };
    fieldType = typeMap[fieldType] ?? field.fieldType;
  }

  switch (fieldType) {
    case FieldType.Boolean:
      return (
        <BooleanFieldRenderer
          value={field.value}
          addToActionItems={field.addToActionItems}
          onChange={handleSimpleFieldChange}
          fieldName={field.fieldName}
        />
      );

    case FieldType.Select:
      return (
        <SelectFieldRenderer
          value={field.value}
          addToActionItems={field.addToActionItems}
          onChange={handleSimpleFieldChange}
          fieldName={field.fieldName}
          options={options}
        />
      );

    case FieldType.Multiselect:
      return (
        <MultiselectFieldRenderer
          entries={field.entries || []}
          onChange={handleEntriesChange}
          fieldName={field.fieldName}
          options={options}
        />
      );

    case FieldType.Text:
      return (
        <TextFieldRenderer
          value={field.value}
          addToActionItems={field.addToActionItems}
          onChange={handleSimpleFieldChange}
          fieldName={field.fieldName}
          multiline
        />
      );

    case FieldType.TextArray:
      return <TextArrayFieldRenderer entries={field.entries || []} onChange={handleEntriesChange} fieldName={field.fieldName} />;

    case FieldType.Date:
      return (
        <DateFieldRenderer
          value={field.value}
          addToActionItems={field.addToActionItems}
          onChange={handleSimpleFieldChange}
          fieldName={field.fieldName}
          label={formatFieldName(field.fieldName)}
        />
      );

    case FieldType.Number:
      return (
        <NumberFieldRenderer
          value={field.value}
          addToActionItems={field.addToActionItems}
          onChange={handleSimpleFieldChange}
          fieldName={field.fieldName}
          label={formatFieldName(field.fieldName)}
        />
      );

    default:
      return (
        <Box sx={{ p: 2, bgcolor: 'warning.light', borderRadius: 1 }}>
          <Typography variant='body2' color='warning.dark'>
            Unknown field type: {field.fieldType}
          </Typography>
        </Box>
      );
  }
};

// Helper function to format field names
const formatFieldName = (fieldName: string): string => {
  // Convert camelCase or PascalCase to Title Case with spaces
  return fieldName
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, str => str.toUpperCase())
    .trim();
};
